import type {
  RuntimeAttachment,
  RuntimeAttachmentOwner,
  RuntimeUploadedFileDto,
} from '@/features/shared/chat/types'

const DEFAULT_MAX_FILE_COUNT = 5
const BYTES_PER_MB = 1024 * 1024
const IMAGE_FILE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg']
const AUDIO_FILE_EXTENSIONS = ['.mp3', '.wav', '.m4a', '.aac', '.flac', '.ogg']
const VIDEO_FILE_EXTENSIONS = ['.mp4', '.mov', '.avi', '.webm', '.mkv', '.mpeg']
const DOCUMENT_FILE_EXTENSIONS = [
  '.csv',
  '.doc',
  '.docx',
  '.eml',
  '.epub',
  '.html',
  '.md',
  '.mdx',
  '.msg',
  '.pdf',
  '.ppt',
  '.pptx',
  '.txt',
  '.xls',
  '.xlsx',
  '.xml',
]

const DOCUMENT_ACCEPT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/*',
]

const TYPE_ACCEPT_MAP: Record<string, string[]> = {
  audio: ['audio/*'],
  document: DOCUMENT_ACCEPT_TYPES,
  image: ['image/*'],
  video: ['video/*'],
}

export interface RuntimeFileUploadPolicy {
  accept?: string
  allowedFileExtensions: string[]
  allowedMimeTypes: string[]
  allowedFileTypes: string[]
  allowedFileUploadMethods: string[]
  enabled: boolean
  maxCount: number
  maxSizeBytes?: number
  maxSizeMb?: number
}

export type RuntimeFileValidationIssue =
  | { code: 'count'; maxCount: number }
  | { code: 'extension'; extensions: string[] }
  | { code: 'method' }
  | { code: 'mime'; mimeTypes: string[] }
  | { code: 'size'; maxSizeMb: number }
  | { code: 'type'; types: string[] }

export interface ResolveRuntimeFilePolicyOptions {
  defaultEnabled?: boolean
  maxCount?: number
}

function isRecordValue(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readString(value: unknown) {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
}

function readNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const numberValue = Number(value)

    return Number.isFinite(numberValue) ? numberValue : undefined
  }

  return undefined
}

function readStringArray(value: unknown) {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter((item) => item.length > 0)
}

function hasUnsafeUrlCharacter(value: string) {
  return Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0) ?? 0

    return character === '\\' || codePoint <= 0x1f || codePoint === 0x7f
  })
}

function normalizeRuntimeAttachmentUrl(value: string | undefined) {
  const url = value?.trim().replace(/&amp;/g, '&')

  if (!url) {
    return undefined
  }

  if (hasUnsafeUrlCharacter(url)) {
    return undefined
  }

  if (url.startsWith('//')) {
    try {
      const parsedUrl = new URL(`https:${url}`)

      return parsedUrl.protocol === 'https:' ? parsedUrl.toString() : undefined
    } catch {
      return undefined
    }
  }

  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) {
    try {
      const parsedUrl = new URL(url)

      return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'
        ? parsedUrl.toString()
        : undefined
    } catch {
      return undefined
    }
  }

  if (/^[a-z0-9.-]+\.[a-z]{2,}(?::\d+)?(?:\/|$)/i.test(url)) {
    return `https://${url}`
  }

  return url
}

function readBoolean(value: unknown) {
  return typeof value === 'boolean' ? value : undefined
}

function readNestedPolicyNode(config: Record<string, unknown>) {
  const candidates = ['image', 'file', 'document', 'audio', 'video']

  return candidates.map((key) => config[key]).find(isRecordValue)
}

function readEnabledNestedPolicyNode(config: Record<string, unknown>) {
  const candidates = ['image', 'file', 'document', 'audio', 'video']

  return candidates
    .map((key) => config[key])
    .find((node): node is Record<string, unknown> => isRecordValue(node) && node.enabled === true)
}

function readPolicyStringArray(
  root: Record<string, unknown>,
  nested: Record<string, unknown> | undefined,
  key: string,
) {
  const rootValue = readStringArray(root[key])

  if (rootValue.length > 0) {
    return rootValue
  }

  const rootFileUploadConfig = isRecordValue(root.fileUploadConfig)
    ? readStringArray(root.fileUploadConfig[key])
    : []

  if (rootFileUploadConfig.length > 0) {
    return rootFileUploadConfig
  }

  const nestedValue = readStringArray(nested?.[key])

  if (nestedValue.length > 0) {
    return nestedValue
  }

  return isRecordValue(nested?.fileUploadConfig)
    ? readStringArray(nested.fileUploadConfig[key])
    : []
}

function readPolicyNumber(
  root: Record<string, unknown>,
  nested: Record<string, unknown> | undefined,
  keys: string[],
) {
  for (const key of keys) {
    const rootValue = readNumber(root[key])

    if (rootValue !== undefined) {
      return rootValue
    }

    if (isRecordValue(root.fileUploadConfig)) {
      const rootConfigValue = readNumber(root.fileUploadConfig[key])

      if (rootConfigValue !== undefined) {
        return rootConfigValue
      }
    }

    const nestedValue = readNumber(nested?.[key])

    if (nestedValue !== undefined) {
      return nestedValue
    }

    if (isRecordValue(nested?.fileUploadConfig)) {
      const nestedConfigValue = readNumber(nested.fileUploadConfig[key])

      if (nestedConfigValue !== undefined) {
        return nestedConfigValue
      }
    }
  }

  return undefined
}

function normalizeExtension(extension: string) {
  const trimmedExtension = extension.trim().toLowerCase()

  if (!trimmedExtension) {
    return ''
  }

  return trimmedExtension.startsWith('.') ? trimmedExtension : `.${trimmedExtension}`
}

function normalizeMimeType(mimeType: string) {
  return mimeType.trim().toLowerCase()
}

function uniq(values: string[]) {
  return Array.from(new Set(values.filter((value) => value.length > 0)))
}

function resolveAccept(
  allowedFileTypes: string[],
  allowedFileExtensions: string[],
  allowedMimeTypes: string[],
) {
  const acceptValues = [
    ...allowedFileExtensions,
    ...allowedMimeTypes,
    ...allowedFileTypes.flatMap((type) => TYPE_ACCEPT_MAP[type] ?? []),
  ]

  return acceptValues.length > 0 ? uniq(acceptValues).join(',') : undefined
}

/**
 * 将 runtime view-config 或 start form constraints 归一为前端上传策略。
 * Dify 的 `file_upload` 既可能是扁平对象，也可能把 image/file 配置放在子节点里。
 */
export function resolveRuntimeFileUploadPolicy(
  config: Record<string, unknown> | null | undefined,
  options: ResolveRuntimeFilePolicyOptions = {},
): RuntimeFileUploadPolicy {
  const root = config ?? {}
  const enabledNested = readEnabledNestedPolicyNode(root)
  const nested = enabledNested ?? readNestedPolicyNode(root)
  const rootEnabled = readBoolean(root.enabled)
  const nestedEnabled = readBoolean(nested?.enabled)
  const nestedPolicyKeys = new Set(['image', 'file', 'document', 'audio', 'video'])
  const hasRootUploadPolicyFields = Object.keys(root).some(
    (key) => key !== 'enabled' && !nestedPolicyKeys.has(key),
  )
  const hasNestedUploadPolicyFields = nested
    ? Object.keys(nested).some((key) => key !== 'enabled')
    : false
  const enabled =
    rootEnabled ??
    nestedEnabled ??
    options.defaultEnabled ??
    (enabledNested ? true : hasRootUploadPolicyFields || hasNestedUploadPolicyFields)

  let allowedFileTypes = readPolicyStringArray(root, nested, 'allowed_file_types').map((type) =>
    type.toLowerCase(),
  )

  if (
    allowedFileTypes.length === 0 &&
    enabledNested !== undefined &&
    enabledNested === root.image
  ) {
    allowedFileTypes = ['image']
  }

  const allowedFileExtensions = readPolicyStringArray(root, nested, 'allowed_file_extensions').map(
    normalizeExtension,
  )
  const allowedMimeTypes = readPolicyStringArray(root, nested, 'allowed_mime_types').map(
    normalizeMimeType,
  )
  const allowedFileUploadMethods =
    readPolicyStringArray(root, nested, 'allowed_file_upload_methods').length > 0
      ? readPolicyStringArray(root, nested, 'allowed_file_upload_methods')
      : ['local_file']
  const resolvedMaxCount =
    options.maxCount ??
    readPolicyNumber(root, nested, [
      'number_limits',
      'max_count',
      'max_length',
      'workflow_file_upload_limit',
    ]) ??
    DEFAULT_MAX_FILE_COUNT
  const maxSizeBytes = readPolicyNumber(root, nested, [
    'max_size_bytes',
    'file_size_limit_bytes',
    'size_limit_bytes',
  ])
  const maxSizeMb = readPolicyNumber(root, nested, [
    'file_size_limit',
    'size_limit',
    'image_file_size_limit',
    'video_file_size_limit',
    'audio_file_size_limit',
  ])

  return {
    accept: resolveAccept(allowedFileTypes, allowedFileExtensions, allowedMimeTypes),
    allowedFileExtensions: uniq(allowedFileExtensions),
    allowedMimeTypes: uniq(allowedMimeTypes),
    allowedFileTypes: uniq(allowedFileTypes),
    allowedFileUploadMethods: uniq(allowedFileUploadMethods),
    enabled,
    maxCount: Math.max(1, Math.floor(resolvedMaxCount)),
    maxSizeBytes,
    maxSizeMb: maxSizeMb ?? (maxSizeBytes ? maxSizeBytes / BYTES_PER_MB : undefined),
  }
}

export function getFileExtension(filename: string) {
  const dotIndex = filename.lastIndexOf('.')

  return dotIndex >= 0 ? filename.slice(dotIndex).toLowerCase() : ''
}

function getRuntimeFileTypeFromExtension(extension: string) {
  if (IMAGE_FILE_EXTENSIONS.includes(extension)) {
    return 'image'
  }

  if (VIDEO_FILE_EXTENSIONS.includes(extension)) {
    return 'video'
  }

  if (AUDIO_FILE_EXTENSIONS.includes(extension)) {
    return 'audio'
  }

  if (DOCUMENT_FILE_EXTENSIONS.includes(extension)) {
    return 'document'
  }

  return undefined
}

function getRuntimeMediaFileTypeFromMimeType(mimeType: string) {
  if (mimeType.startsWith('image/')) {
    return 'image'
  }

  if (mimeType.startsWith('video/')) {
    return 'video'
  }

  if (mimeType.startsWith('audio/')) {
    return 'audio'
  }

  return undefined
}

function isKnownDocumentMimeType(mimeType: string) {
  return (
    mimeType.startsWith('text/') ||
    mimeType.includes('pdf') ||
    mimeType.includes('document') ||
    mimeType.includes('spreadsheet') ||
    mimeType.includes('presentation') ||
    mimeType.includes('msword') ||
    mimeType.includes('ms-excel') ||
    mimeType.includes('ms-powerpoint') ||
    mimeType === 'application/epub+zip' ||
    mimeType === 'message/rfc822'
  )
}

export function getRuntimeFileType(file: File) {
  const mimeType = file.type.toLowerCase()
  const mediaFileType = getRuntimeMediaFileTypeFromMimeType(mimeType)

  if (mediaFileType) {
    return mediaFileType
  }

  const extension = getFileExtension(file.name)
  const extensionFileType = getRuntimeFileTypeFromExtension(extension)

  if (extensionFileType) {
    return extensionFileType
  }

  if (isKnownDocumentMimeType(mimeType)) {
    return 'document'
  }

  return 'file'
}

function matchesAllowedMimeType(fileMimeType: string, allowedMimeTypes: string[]) {
  const normalizedFileMimeType = normalizeMimeType(fileMimeType)

  if (!normalizedFileMimeType) {
    return false
  }

  return allowedMimeTypes.some((allowedMimeType) => {
    if (allowedMimeType === '*' || allowedMimeType === '*/*') {
      return true
    }

    if (allowedMimeType.endsWith('/*')) {
      const prefix = allowedMimeType.slice(0, -1)

      return normalizedFileMimeType.startsWith(prefix)
    }

    return normalizedFileMimeType === allowedMimeType
  })
}

function shouldValidateRuntimeFileExtension(
  fileType: string,
  fileExtension: string,
  allowedFileExtensions: string[],
  allowedFileTypes: string[],
) {
  if (allowedFileExtensions.length === 0) {
    return false
  }

  if (allowedFileTypes.length === 0) {
    return true
  }

  if (allowedFileTypes.includes('file') || allowedFileTypes.includes('*')) {
    return true
  }

  const fileExtensionType = getRuntimeFileTypeFromExtension(fileExtension)

  if (!fileExtensionType || fileExtensionType !== fileType) {
    return true
  }

  return allowedFileExtensions.some((extension) => {
    const allowedExtensionType = getRuntimeFileTypeFromExtension(extension)

    return allowedExtensionType === undefined || allowedExtensionType === fileType
  })
}

/**
 * 发送上传前的前端预校验。后端/provider 仍是最终接收方和权限边界。
 */
export function validateRuntimeFile(
  file: File,
  policy: RuntimeFileUploadPolicy,
  currentCount: number,
): RuntimeFileValidationIssue | undefined {
  if (currentCount >= policy.maxCount) {
    return { code: 'count', maxCount: policy.maxCount }
  }

  if (!policy.allowedFileUploadMethods.includes('local_file')) {
    return { code: 'method' }
  }

  const fileType = getRuntimeFileType(file)
  const extension = getFileExtension(file.name)

  if (
    policy.allowedFileTypes.length > 0 &&
    !policy.allowedFileTypes.includes(fileType) &&
    !policy.allowedFileTypes.includes('file') &&
    !policy.allowedFileTypes.includes('*')
  ) {
    return { code: 'type', types: policy.allowedFileTypes }
  }

  if (
    policy.allowedMimeTypes.length > 0 &&
    !matchesAllowedMimeType(file.type, policy.allowedMimeTypes)
  ) {
    return { code: 'mime', mimeTypes: policy.allowedMimeTypes }
  }

  if (
    shouldValidateRuntimeFileExtension(
      fileType,
      extension,
      policy.allowedFileExtensions,
      policy.allowedFileTypes,
    )
  ) {
    if (!policy.allowedFileExtensions.includes(extension)) {
      return { code: 'extension', extensions: policy.allowedFileExtensions }
    }
  }

  const maxSizeBytes =
    policy.maxSizeBytes ?? (policy.maxSizeMb ? policy.maxSizeMb * BYTES_PER_MB : undefined)

  if (maxSizeBytes && file.size > maxSizeBytes) {
    return { code: 'size', maxSizeMb: policy.maxSizeMb ?? maxSizeBytes / BYTES_PER_MB }
  }

  return undefined
}

export function createRuntimeAttachmentFromUpload(
  file: File,
  upload: RuntimeUploadedFileDto,
  previewUrl?: string,
): RuntimeAttachment {
  return {
    mime_type: upload.mime_type ?? file.type,
    name: upload.name || file.name,
    preview_url: previewUrl,
    size: upload.size ?? file.size,
    transfer_method: 'local_file',
    type: getRuntimeFileType(file),
    upload_file_id: upload.id,
    url: '',
  }
}

function readAttachmentNumber(value: unknown) {
  const numberValue = readNumber(value)

  return numberValue === undefined ? undefined : Math.max(0, numberValue)
}

/**
 * 将历史消息、SSE 文件事件和 provider 原始文件对象收敛为 UI 可渲染附件。
 */
export function normalizeRuntimeAttachment(
  value: unknown,
  fallbackOwner?: RuntimeAttachmentOwner,
): RuntimeAttachment | undefined {
  if (!isRecordValue(value)) {
    return undefined
  }

  const uploadFileId = readString(value.upload_file_id ?? value.file_id)
  const id = readString(value.id) ?? uploadFileId
  const url = normalizeRuntimeAttachmentUrl(readString(value.url))
  const previewUrl = normalizeRuntimeAttachmentUrl(
    readString(value.preview_url ?? value.previewUrl),
  )
  const name = readString(value.name ?? value.filename ?? value.file_name)
  const filename = readString(value.filename ?? value.file_name ?? value.name)
  const transferMethod = readString(value.transfer_method)
  const type = readString(value.type)
  const mimeType = readString(value.mime_type ?? value.mime)
  const belongsTo = readString(value.belongs_to) ?? fallbackOwner

  if (!id && !uploadFileId && !url && !name && !filename) {
    return undefined
  }

  return {
    belongs_to: belongsTo,
    filename,
    id,
    mime_type: mimeType,
    name,
    preview_url: previewUrl,
    size: readAttachmentNumber(value.size),
    transfer_method: transferMethod,
    type,
    upload_file_id: uploadFileId,
    url,
  }
}

export function normalizeRuntimeAttachments(
  value: unknown,
  fallbackOwner?: RuntimeAttachmentOwner,
) {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((item) => normalizeRuntimeAttachment(item, fallbackOwner))
    .filter((item): item is RuntimeAttachment => Boolean(item))
}

export function getRuntimeAttachmentDisplayName(attachment: RuntimeAttachment) {
  return (
    attachment.name ??
    attachment.filename ??
    attachment.upload_file_id ??
    attachment.id ??
    attachment.url ??
    'file'
  )
}

export function getRuntimeAttachmentKey(attachment: RuntimeAttachment, index: number) {
  return (
    attachment.upload_file_id ??
    attachment.id ??
    attachment.url ??
    `${getRuntimeAttachmentDisplayName(attachment)}-${String(index)}`
  )
}

export function isRuntimeImageAttachment(attachment: RuntimeAttachment) {
  const type = attachment.type?.toLowerCase()
  const mimeType = attachment.mime_type?.toLowerCase()
  const extension = getFileExtension(getRuntimeAttachmentDisplayName(attachment))

  return (
    type === 'image' ||
    Boolean(mimeType?.startsWith('image/')) ||
    IMAGE_FILE_EXTENSIONS.includes(extension)
  )
}

export function getRuntimeAttachmentPreviewUrl(attachment: RuntimeAttachment) {
  return attachment.preview_url ?? attachment.url
}

export function mergeRuntimeAttachments(
  currentAttachments: RuntimeAttachment[],
  nextAttachments: RuntimeAttachment[],
) {
  const seenKeys = new Set<string>()
  const merged: RuntimeAttachment[] = []

  for (const attachment of [...currentAttachments, ...nextAttachments]) {
    const key =
      attachment.upload_file_id ??
      attachment.id ??
      attachment.url ??
      getRuntimeAttachmentDisplayName(attachment)

    if (seenKeys.has(key)) {
      continue
    }

    seenKeys.add(key)
    merged.push(attachment)
  }

  return merged
}

/**
 * 生成发送给 runtime chat/start form 的 provider 兼容文件对象。
 */
export function serializeRuntimeAttachmentForSend(attachment: RuntimeAttachment) {
  const transferMethod = attachment.transfer_method ?? 'local_file'

  if (transferMethod === 'remote_url') {
    if (!attachment.url) {
      return undefined
    }

    return {
      name: getRuntimeAttachmentDisplayName(attachment),
      transfer_method: transferMethod,
      type: attachment.type ?? 'image',
      url: attachment.url,
    }
  }

  if (!attachment.upload_file_id) {
    return undefined
  }

  return {
    name: getRuntimeAttachmentDisplayName(attachment),
    transfer_method: 'local_file',
    type: attachment.type ?? 'document',
    upload_file_id: attachment.upload_file_id,
    url: '',
  }
}

function readMarkdownLink(line: string) {
  const match = /\[([^\]\n]+)\]\(([^)\s]+)(?:\s+["'][^"']*["'])?\)/.exec(line)

  if (!match) {
    return undefined
  }

  return {
    endIndex: match.index + match[0].length,
    label: match[1].trim(),
    startIndex: match.index,
    url: normalizeRuntimeAttachmentUrl(match[2]),
  }
}

// Reads a display name from an attachment Markdown link, falling back to the URL filename when the label is generic.
function readAttachmentNameFromMarkdown(label: string, url: string) {
  const labelName = label.replace(/^(打开附件|Open attachment)\s*/i, '').trim()

  if (labelName) {
    return labelName
  }

  try {
    const parsed = new URL(url, 'https://placeholder.local')
    const filename = parsed.pathname.split('/').filter(Boolean).at(-1)

    return filename ? decodeURIComponent(filename) : label
  } catch {
    return label
  }
}

function extractAttachmentFromMarkdownLine(line: string, fallbackOwner?: RuntimeAttachmentOwner) {
  const link = readMarkdownLink(line)

  if (!link?.url) {
    return undefined
  }

  const outsideLinkText = `${line.slice(0, link.startIndex)}${line.slice(link.endIndex)}`
    .replace(/^\s*[-*]\s*/, '')
    .trim()
  const hasAttachmentPrefix = /^(文件|附件|File|Attachment)\s*[：:]?\s*$/i.test(outsideLinkText)
  const hasAttachmentLabel = /^(打开附件|Open attachment)\b/i.test(link.label)
  const isStandaloneAttachmentLink = outsideLinkText.length === 0 && hasAttachmentLabel

  if (!hasAttachmentPrefix && !isStandaloneAttachmentLink) {
    return undefined
  }

  const name = readAttachmentNameFromMarkdown(link.label, link.url)

  return {
    belongs_to: fallbackOwner,
    name,
    url: link.url,
  } satisfies RuntimeAttachment
}

export function extractRuntimeAttachmentsFromMarkdown(
  markdown: string,
  fallbackOwner?: RuntimeAttachmentOwner,
) {
  const attachments: RuntimeAttachment[] = []
  const lines: string[] = []

  for (const line of markdown.split(/\r?\n/)) {
    const attachment = extractAttachmentFromMarkdownLine(line, fallbackOwner)

    if (attachment) {
      attachments.push(attachment)
      continue
    }

    lines.push(line)
  }

  return {
    attachments,
    content: lines
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim(),
  }
}
